import {IconButton} from 'platform/components';
import {Box, HStack, Icon, Show, Text, ThemeIconKey} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {ClaraClaimAttachment} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

interface ClaraClaimAttachmentItemProps extends TestIdProps {
  attachment: ClaraClaimAttachment;
  onRemove?: () => void;
  onPreview?: () => void;
  isReadOnly?: boolean;
}

function getFileIcon(contentType: string, filename: string): ThemeIconKey {
  const ext = filename.split('.').pop()?.toUpperCase();

  return match<string, ThemeIconKey>(contentType)
    .with('application/pdf', () => 'files_default/file_type_PDF')
    .with('image/jpeg', () => 'files_default/file_type_JPG')
    .with('image/png', () => 'files_default/file_type_PNG')
    .with('image/gif', () => 'files_default/file_type_JPG')
    .with('text/csv', () => 'files_default/file_type_CSV')
    .otherwise(() =>
      match(ext)
        .with('PNG', () => 'files_default/file_type_PNG' as ThemeIconKey)
        .with(Pattern.union('JPG', 'JPEG'), () => 'files_default/file_type_JPG' as ThemeIconKey)
        .with('PDF', () => 'files_default/file_type_PDF' as ThemeIconKey)
        .otherwise(() => 'files_default/file_type_UNKNOWN' as ThemeIconKey)
    );
}

export function ClaraClaimAttachmentItem(props: ClaraClaimAttachmentItemProps) {
  const iconValue = getFileIcon(props.attachment.contentType, props.attachment.filename);

  return (
    <HStack spacing={2} align="center" width="100%">
      <Icon value={iconValue} size={8} />
      <Box flexGrow={1} minWidth={0}>
        <Text size="small" noWrap>
          {props.attachment.filename}
        </Text>
      </Box>
      <Show when={!props.isReadOnly}>
        <IconButton
          icon="navigation/close"
          size="small"
          priority="primary"
          severity="danger"
          onClick={props.onRemove}
          aria-label={i18n.t('general.actions.remove')}
          data-testid={suffixTestId('remove', props)}
        />
      </Show>
      <Show when={props.isReadOnly}>
        <IconButton
          icon="action/visibility"
          size="small"
          onClick={props.onPreview}
          aria-label={i18n.t('general.actions.preview')}
          data-testid={suffixTestId('preview', props)}
        />
      </Show>
    </HStack>
  );
}
